import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, Button, TableSortLabel, LinearProgress,
  FormControlLabel, Switch, Tabs, Tab,
} from '@mui/material';
import useSortableTable from '../utils/useSortableTable';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Refresh as RefreshIcon,
  Send as DeliverIcon,
  VpnKey as KDMIcon,
  Folder as FolderIcon,
  Archive as PackageIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Lock as LockIcon,
  Warning as WarningIcon,
  VerifiedUser as QCIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Unarchive as UnarchiveIcon,
  Clear as ClearIcon,
  Insights as AnalyticsIcon,
} from '@mui/icons-material';
import { InputAdornment, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import {
  computeDcpAggregates,
  isDcpStalled,
  isDcpAwaitingKdm,
  shouldShowDcpBulkOps,
  isDcpBulkOpsForced,
  setDcpBulkOpsForced,
} from '../utils/library';
import { useBulkRunner } from '../utils/bulkOps';
import BulkActionBar from '../components/BulkActionBar';
import { Checkbox } from '@mui/material';
import api from '../services/api';
import DCPDeliveryAnalyticsDrawer from '../components/DCPDeliveryAnalyticsDrawer';
import PipelineProgress from '../components/PipelineProgress';
import { Drawer, Divider, Collapse, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar } from '@mui/material';
import { deleteDCPJob, renameDCPJob, restoreDCPJob } from '../store/slices/dcpJobsSlice';
import { revokePackage } from '../store/slices/deliverySlice';
import { fetchDCPJobs } from '../store/slices/dcpJobsSlice';
import { fetchPackages } from '../store/slices/deliverySlice';
import { fetchKDMRecords } from '../store/slices/kdmSlice';
import DeliverDialog from '../components/DeliverDialog';
import DCPDistributeDialog from '../components/DCPDistributeDialog';
import { fmtDateTime } from '../utils/formatDate';
import { formatRelativeTime } from '../utils/library';

function DCPLibrary() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { jobs } = useSelector((state) => state.dcpJobs);
  const { packages } = useSelector((state) => state.delivery);
  const { records: kdmRecords } = useSelector((state) => state.kdm);
  const { activeCompanyId, list } = useSelector((s) => s.company);
  const { userInfo } = useSelector((s) => s.auth);
  const memberRole = list.find((c) => c.id === activeCompanyId)?.role;
  const canDelete = userInfo?.is_superadmin || (memberRole && memberRole !== 'venue_manager');
  const [deliverJob, setDeliverJob] = useState(null);
  const [deliverPhysical, setDeliverPhysical] = useState(false);
  const [distributeJob, setDistributeJob] = useState(null);
  const [analyticsDcp, setAnalyticsDcp] = useState(null);
  const [qcDrawer, setQcDrawer] = useState(null); // { job, report }
  const [expandedRows, setExpandedRows] = useState({});
  // Per-row expanded-detail tab. Default 'delivery' — most users
  // open a row to ask "did it get to the theater?" rather than to
  // grub through MD5 hashes. Technical tab keeps the engineering
  // surface (verification hashes, raw transfer logs, format pills,
  // source file detail) one click away for operators who need it.
  const [detailTab, setDetailTab] = useState({});
  const toggleRow = (id) => setExpandedRows(s => ({ ...s, [id]: !s[id] }));
  const openQcDrawer = async (job) => {
    setQcDrawer({ job, report: null, loading: true });
    try {
      const { data } = await api.get(`/api/dcp-jobs/${job.id}/qc-report`);
      setQcDrawer({ job, report: data });
    } catch (_e) {
      setQcDrawer({ job, report: null, error: 'Failed to load QC report' });
    }
  };
  const rerunQc = async (jobId) => {
    setQcDrawer(d => ({ ...d, loading: true, error: null, qcProgress: 'Starting verification…' }));
    try {
      await api.post(`/api/dcp-jobs/${jobId}/qc-rerun`);
      const startedAt = Date.now();
      const MAX_WAIT_MS = 15 * 60 * 1000;
      const is4K = qcDrawer?.job?.resolution === '4K';
      const estimate = is4K ? '8–12 minutes' : '2–4 minutes';
      setQcDrawer(d => ({ ...d, qcProgress: `Running structural and conformance checks (typically ${estimate})…` }));
      while (Date.now() - startedAt < MAX_WAIT_MS) {
        await new Promise(r => setTimeout(r, 5000));
        const elapsed = Math.round((Date.now() - startedAt) / 1000);
        const min = Math.floor(elapsed / 60);
        const sec = elapsed % 60;
        const timeStr = min > 0 ? `${min}m ${sec}s` : `${sec}s`;
        setQcDrawer(d => ({ ...d, qcProgress: `Verifying DCP integrity and compliance — ${timeStr} elapsed (typically ${estimate})…` }));
        const { data } = await api.get(`/api/dcp-jobs/${jobId}/qc-report`);
        if (data?.qc_status && data.qc_status !== 'pending') {
          setQcDrawer(d => ({ ...d, report: data, loading: false, qcProgress: null }));
          dispatch(fetchDCPJobs());
          return;
        }
      }
      setQcDrawer(d => ({ ...d, loading: false, qcProgress: null, error: 'QC is still running. Refresh the page to check again.' }));
    } catch (_e) {
      setQcDrawer(d => ({ ...d, loading: false, qcProgress: null, error: 'Re-run failed' }));
    }
  };
  const qcChipColor = (s) => s === 'pass' ? 'success' : s === 'warn' ? 'warning' : s === 'fail' ? 'error' : s === 'pending' ? 'info' : 'default';

  const [kdmEmailDialog, setKdmEmailDialog] = useState(null);
  const [kdmEmailForm, setKdmEmailForm] = useState({ cc: '', message: '', attach: false });
  // IP enrichment cache: ip → {isp, city, region, country_name, is_mobile, is_proxy, is_hosting, asn}
  // Lazy-fetched when activity logs are visible. Single map for the
  // whole page so multiple share rows share lookups.
  const [ipInfo, setIpInfo] = useState({});
  // Inline rename: tracks which dcp.id is being edited and its draft text.
  const [renameId, setRenameId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameError, setRenameError] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const beginRename = (dcp) => {
    setRenameId(dcp.id); setRenameDraft(dcp.name || ''); setRenameError(''); setRenameSaving(false);
  };
  const cancelRename = () => {
    setRenameId(null); setRenameDraft(''); setRenameError(''); setRenameSaving(false);
  };
  const commitRename = async (dcp) => {
    const next = renameDraft.trim();
    if (!next) { setRenameError('Name cannot be empty'); return; }
    if (next === dcp.name) { cancelRename(); return; }
    if (next.length > 80) { setRenameError('Max 80 characters'); return; }
    setRenameSaving(true); setRenameError('');
    try {
      await dispatch(renameDCPJob({ jobId: dcp.id, name: next })).unwrap();
      cancelRename();
    } catch (e) {
      setRenameError(typeof e === 'string' ? e : (e?.message || 'Failed to rename'));
      setRenameSaving(false);
    }
  };
  // When the visible packages change, collect every public IP across
  // all share events and fire one batch lookup to /api/delivery/ip-info.
  // The endpoint serves cached results instantly and only hits the
  // external geolocation API for truly-new IPs.
  useEffect(() => {
    const ips = new Set();
    for (const pkg of (packages || [])) {
      for (const sh of (pkg.shares || [])) {
        for (const ev of (sh.events || [])) {
          const ip = (ev.ip_address || '').trim();
          if (!ip) continue;
          // Skip private ranges client-side — server filters too,
          // but no point sending those across the wire.
          if (ip.startsWith('192.168.') || ip.startsWith('10.') ||
              ip.startsWith('172.16.') || ip.startsWith('127.') ||
              ip === '::1' || ip === 'unknown') continue;
          if (!(ip in ipInfo)) ips.add(ip);
        }
      }
    }
    if (ips.size === 0) return;
    const list = Array.from(ips);
    api.get('/api/delivery/ip-info', { params: { ips: list.join(',') } })
      .then(({ data }) => setIpInfo(prev => ({ ...prev, ...data })))
      .catch(() => { /* enrichment is non-critical */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packages]);
  // v10.21.57: the inline Active Transfers panel + its 10s polling
  // were removed from this page. Operators wanted the live-transfer
  // signal where they manage deliveries (Dashboard + Distribution),
  // not buried under the archive. The rich panel lives at
  // <ActiveTransfersPanel sourceType="dcp" /> and renders on those
  // two surfaces now.

  // Show-archived toggle: default off so the active Library stays
  // focused. Operators flip it on to see soft-archived jobs and
  // restore them.
  const [showArchived, setShowArchived] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Bulk-ops state — same threshold-gated pattern as Watermarks
  // Library, distinct localStorage key per utils/library.js so the
  // pages don't share visibility state.
  const [bulkForce, setBulkForce] = useState(isDcpBulkOpsForced());
  const [bulkAction, setBulkAction] = useState(null);
  const toggleBulkForce = () => {
    const next = !bulkForce;
    setBulkForce(next);
    setDcpBulkOpsForced(next);
  };
  const bulk = useBulkRunner({
    onComplete: ({ failed, total }) => {
      dispatch(fetchDCPJobs({ includeArchived: showArchived }));
      dispatch(fetchPackages({ sourceType: 'dcp' }));
      if (failed > 0) {
        setSnackbar({
          open: true,
          message: `${failed} of ${total} bulk operations failed.`,
          severity: 'warning',
        });
      }
    },
  });
  const selectedDcpIds = bulk.selectedIds;

  useEffect(() => {
    dispatch(fetchDCPJobs({ includeArchived: showArchived }));
    dispatch(fetchPackages({ sourceType: 'dcp' }));
    dispatch(fetchKDMRecords({}));
  }, [dispatch, showArchived]);

  // Live-refresh DCP rows while any QC is in flight (running or pending).
  // Without this the qc_progress on the Library only updates on manual
  // refresh, which makes a re-running QC look stuck. Stops polling as
  // soon as everything's terminal so we don't burn bandwidth on idle
  // libraries.
  const anyQcInFlight = (jobs || []).some(
    (j) => j.qc_status === 'running' || j.qc_status === 'pending'
  );
  useEffect(() => {
    if (!anyQcInFlight) return undefined;
    const iv = setInterval(() => dispatch(fetchDCPJobs()), 3000);
    return () => clearInterval(iv);
  }, [dispatch, anyQcInFlight]);

  // When a row is expanded, refresh KDM records and packages so the lists are current
  useEffect(() => {
    const ids = Object.entries(expandedRows).filter(([, v]) => v).map(([k]) => k);
    if (ids.length === 0) return;
    dispatch(fetchKDMRecords({}));
    dispatch(fetchPackages({ sourceType: 'dcp' }));
  }, [dispatch, expandedRows]);

  // eslint-disable-next-line no-unused-vars
  const getActiveShare = (pkg) => (pkg?.shares || []).find(s => !s.revoked_at);

  // Library shows DCPs that are actually deliverable: status=completed AND
  // the package has synced to MinIO (delivery_ready=true). Until sync
  // finishes, the row stays in the Queue so operators don't see a row in
  // the Library before they can actually deliver. Failed/cancelled never
  // reach this page.
  const completedDCPsRaw = (jobs || []).filter(
    (j) => j.status === 'completed' && j.delivery_ready === true
  );
  const dcpQ = (searchTerm || '').trim().toLowerCase();
  const completedDCPsSearched = dcpQ
    ? completedDCPsRaw.filter((j) =>
        (j.name || '').toLowerCase().includes(dcpQ) ||
        (j.source_filename || '').toLowerCase().includes(dcpQ) ||
        (j.unique_id || '').toLowerCase().includes(dcpQ)
      )
    : completedDCPsRaw;
  // Status filter — synthetic options that don't map directly onto
  // j.status. `awaiting_kdm` and `stalled` are computed against the
  // packages + KDM lists; `qc_fail` reads the per-job qc_status.
  const completedDCPsFiltered = (() => {
    if (!statusFilter) return completedDCPsSearched;
    if (statusFilter === 'failed') {
      return completedDCPsSearched.filter((j) => j.status === 'failed');
    }
    if (statusFilter === 'qc_fail') {
      return completedDCPsSearched.filter((j) => j.qc_status === 'fail' || j.qc_status === 'warn');
    }
    if (statusFilter === 'awaiting_kdm') {
      return completedDCPsSearched.filter((j) => isDcpAwaitingKdm(j, packages, kdmRecords));
    }
    if (statusFilter === 'stalled') {
      return completedDCPsSearched.filter((j) => isDcpStalled(j, packages, kdmRecords));
    }
    return completedDCPsSearched;
  })();
  const getPkg = (jobId) => packages.find(p => p.source_job_id === jobId && p.source_type === 'dcp');
  // Bulk-ops gating — at 10+ visible DCPs, show the checkbox column +
  // action bar. Superadmin gear-toggle override in the page header
  // handles small fixture workspaces.
  const bulkVisible = shouldShowDcpBulkOps(
    completedDCPsFiltered.length, !!userInfo?.is_superadmin, bulkForce,
  );

  const { rows: completedDCPs, sortProps } = useSortableTable(
    completedDCPsFiltered,
    'completed_at',
    'desc',
    {
      name: (r) => (r.name || '').toLowerCase(),
      container: (r) => `${r.container || ''} ${r.resolution || ''}`.toLowerCase(),
      audio_layout: (r) => (r.audio_layout || '').toLowerCase(),
      encrypt: (r) => (r.encrypt ? 1 : 0),
      status: (r) => r.status,
      completed_at: (r) => (r.completed_at ? new Date(r.completed_at).getTime() : 0),
      delivered_at: (r) => {
        const p = getPkg(r.id);
        return p?.created_at ? new Date(p.created_at).getTime() : 0;
      },
    },
  );

  // (Dropped helpers cleaned up in v10.4.21: duplicate getPackage,
  // unused formatSize, handleRefreshDelivery / handleDeleteKdm /
  // handleRevokeShare / handleCopyLink / handleResendEmail. All
  // recoverable from git history if a future activity-log refactor
  // wants them back.)

  // Visible-set ids feed bulk select-all helpers. Sort+filter applied.
  const visibleDcpIds = completedDCPs.map((d) => d.id);

  // Wrapped runner so each bulk button can `await` and close the
  // confirm dialog when done.
  const runBulk = async (action) => {
    await bulk.run(action);
    setBulkAction(null);
  };

  const doBulkArchive = () => runBulk((id) => dispatch(deleteDCPJob(id)).unwrap());
  const doBulkRerunQc = () => runBulk((id) => api.post(`/api/dcp-jobs/${id}/qc-rerun`));
  const doBulkRevoke = async () => {
    await runBulk(async (id) => {
      const pkg = (packages || []).find(
        (p) => p.source_type === 'dcp' && p.source_job_id === id && !p.revoked,
      );
      if (!pkg) return;
      await dispatch(revokePackage(pkg.id)).unwrap();
    });
  };

  return (
    <Box>
      <PipelineProgress />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>DCP Library</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="qc_fail">QC fail / warn</MenuItem>
              <MenuItem value="awaiting_kdm">Awaiting KDM</MenuItem>
              <MenuItem value="stalled">Stalled (72h+ no activity)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder="Search project, source, ID, or email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 280 }}
            InputProps={{
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')} aria-label="Clear search">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
            }
            label="Show archived"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: 'text.secondary' } }}
          />
          {!!userInfo?.is_superadmin && (jobs?.length || 0) < 10 && (
            <>
              <Box sx={{ width: '1px', height: 22, bgcolor: 'divider', mx: 1 }} />
              <Tooltip title="Bulk-action checkboxes are normally hidden when there are fewer than 10 DCPs (clutters small workspaces). Toggle on to show them anyway.">
                <FormControlLabel
                  control={<Switch size="small" checked={bulkForce} onChange={toggleBulkForce} />}
                  label="Force bulk actions"
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: 'text.secondary' } }}
                />
              </Tooltip>
            </>
          )}
          <Tooltip title="Refresh">
            <IconButton onClick={() => { dispatch(fetchDCPJobs({ includeArchived: showArchived })); dispatch(fetchPackages({ sourceType: 'dcp' })); }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Aggregate stats — workspace health at a glance. Hidden when
          the workspace has no DCPs to keep the empty state clean. */}
      {(jobs && jobs.filter((j) => j.status === 'completed' || j.status === 'failed' || j.status === 'creating' || j.status === 'building' || j.status === 'packaging').length > 0) && (() => {
        const a = computeDcpAggregates(jobs || [], packages || [], kdmRecords || []);
        return (
          <Box sx={{
            display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center',
            mb: 2, py: 1, px: 1.5, borderRadius: 1,
            border: '1px solid', borderColor: 'divider',
            bgcolor: 'background.paper',
          }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Library</Typography>
              <Typography variant="h6">{a.total}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Today</Typography>
              <Typography variant="h6">{a.completedToday}</Typography>
            </Box>
            {a.inFlight > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">In flight</Typography>
                <Typography variant="h6" color="warning.main">{a.inFlight}</Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary">Distributed</Typography>
              <Typography variant="h6" color="success.main">{a.distributed}</Typography>
            </Box>
            {a.awaitingKdm > 0 && (
              <Tooltip title="Encrypted DCP delivered to a venue but no KDM has been issued. Click to filter.">
                <Box sx={{ cursor: 'pointer' }} onClick={() => setStatusFilter('awaiting_kdm')}>
                  <Typography variant="caption" color="text.secondary">Awaiting KDM</Typography>
                  <Typography variant="h6" color="warning.main">{a.awaitingKdm}</Typography>
                </Box>
              </Tooltip>
            )}
            {a.stalled > 0 && (
              <Tooltip title="Delivered but no recipient signal in 72+ hours. Click to filter.">
                <Box sx={{ cursor: 'pointer' }} onClick={() => setStatusFilter('stalled')}>
                  <Typography variant="caption" color="text.secondary">Stalled</Typography>
                  <Typography variant="h6" color="warning.main">{a.stalled}</Typography>
                </Box>
              </Tooltip>
            )}
            {a.failed > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">Failed</Typography>
                <Typography variant="h6" color="error.main">{a.failed}</Typography>
              </Box>
            )}
          </Box>
        );
      })()}

      {/* v10.21.57: the inline Active Transfers panel that lived here
          moved to the Dashboard + Distribution page via the shared
          <ActiveTransfersPanel> component. Operators wanted the live-
          delivery signal where it's actionable, not under the Library
          archive view. */}

      {/* Bulk action bar — appears when bulkVisible AND ≥1 selected.
          Same concurrency-bounded pattern as Sources/Watermarks.
          Bulk actions: Archive, Re-run QC, Revoke shares. */}
      {bulkVisible && (
        <BulkActionBar
          selectedCount={selectedDcpIds.size}
          busy={bulk.busy}
          progress={bulk.progress}
          onClear={bulk.clearSelection}
          actions={
            <>
              <Button size="small" disabled={bulk.busy}
                onClick={() => setBulkAction({ kind: 'rerun_qc' })}>
                Re-run QC
              </Button>
              <Button size="small" color="warning" disabled={bulk.busy}
                onClick={() => setBulkAction({ kind: 'revoke' })}>
                Revoke shares
              </Button>
              <Button size="small" color="error" variant="contained" disabled={bulk.busy}
                onClick={() => setBulkAction({ kind: 'archive' })}>
                Archive
              </Button>
            </>
          }
        />
      )}

      {completedDCPs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>No completed DCPs yet. Create a DCP from the queue.</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/dcp/create')}>
            Create DCP
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {bulkVisible && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      indeterminate={bulk.someVisibleSelected(visibleDcpIds) && !bulk.allVisibleSelected(visibleDcpIds)}
                      checked={bulk.allVisibleSelected(visibleDcpIds)}
                      onChange={() => bulk.toggleSelectAllVisible(visibleDcpIds)}
                      disabled={completedDCPs.length === 0}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ width: 40 }} />
                <TableCell><TableSortLabel {...sortProps('name')}>Project</TableSortLabel></TableCell>
                <TableCell sx={{ width: 70, whiteSpace: 'nowrap' }}>
                  <Tooltip title="Quality check status (Clairmeta + DCP-o-matic verify)">
                    <span>QC</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: 180 }}>Status</TableCell>
                <TableCell sx={{ width: 220 }}>Sent To</TableCell>
                <TableCell sx={{ width: 200 }}>Last activity</TableCell>
                <TableCell sx={{ width: 120 }}><TableSortLabel {...sortProps('completed_at')}>Created</TableSortLabel></TableCell>
                <TableCell align="right" sx={{ width: 200 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {completedDCPs.map(dcp => {
                const pkg = getPkg(dcp.id);
                const hasPackage = Boolean(dcp.package_path);
                const hasDelivery = Boolean(pkg && !pkg.revoked);
                const shareCount = pkg?.shares?.length || 0;
                const expanded = !!expandedRows[dcp.id];

                return (
                  <React.Fragment key={dcp.id}>
                    <TableRow hover selected={selectedDcpIds.has(dcp.id)} sx={{ '& > *': { borderBottom: expanded ? 'unset' : undefined } }}>
                      {bulkVisible && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={selectedDcpIds.has(dcp.id)}
                            onChange={() => bulk.toggleSelected(dcp.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Tooltip title={
                          shareCount > 0
                            ? `${expanded ? 'Hide' : 'Show'} ${shareCount} deliver${shareCount === 1 ? 'y' : 'ies'}, KDMs, downloads`
                            : `${expanded ? 'Hide' : 'Show'} job details`
                        }>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                            <IconButton size="small" onClick={() => toggleRow(dcp.id)}>
                              {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                            </IconButton>
                            {shareCount > 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', ml: -0.5 }}>
                                {shareCount}
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FolderIcon fontSize="small" color="primary" />
                          {renameId === dcp.id ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                              <TextField
                                size="small"
                                autoFocus
                                value={renameDraft}
                                onChange={(e) => setRenameDraft(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') { e.preventDefault(); commitRename(dcp); }
                                  if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                                }}
                                error={!!renameError}
                                helperText={renameError || ' '}
                                disabled={renameSaving}
                                inputProps={{ maxLength: 80 }}
                                sx={{ minWidth: 240, '& .MuiFormHelperText-root': { mt: 0, fontSize: '0.65rem' } }}
                              />
                              <Tooltip title="Save (Enter)">
                                <span><IconButton size="small" disabled={renameSaving} onClick={() => commitRename(dcp)}>
                                  <CheckIcon fontSize="small" color="success" />
                                </IconButton></span>
                              </Tooltip>
                              <Tooltip title="Cancel (Esc)">
                                <span><IconButton size="small" disabled={renameSaving} onClick={cancelRename}>
                                  <CloseIcon fontSize="small" />
                                </IconButton></span>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" fontWeight={600} sx={{ cursor: 'pointer' }} onClick={() => toggleRow(dcp.id)}>
                                  {dcp.name}
                                </Typography>
                                <Tooltip title="Rename project — display name only; doesn't touch shipped tar filenames">
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); beginRename(dcp); }}
                                    sx={{ opacity: 0.4, '&:hover': { opacity: 1 } }}>
                                    <EditIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              {/* Spec subtitle: format · audio · standard · content type, with
                                  encryption icon inline. Folds the old Encryption column into
                                  the title so unencrypted DCPs flag visibly without burning
                                  a column on a binary value. */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                {dcp.encrypt ? (
                                  <Tooltip title="Encrypted — recipients need a KDM to play"><LockIcon sx={{ fontSize: 12, color: 'text.secondary' }} /></Tooltip>
                                ) : (
                                  <Tooltip title="Unencrypted — anyone with the file can play it">
                                    <Chip
                                      label="UNENCRYPTED"
                                      size="small"
                                      color="warning"
                                      sx={{ height: 14, fontSize: '0.55rem', fontWeight: 700, '& .MuiChip-label': { px: 0.6 } }}
                                    />
                                  </Tooltip>
                                )}
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                  {dcp.resolution} · {dcp.audio_layout === 'stereo' ? 'Stereo' : '5.1'} ·{' '}
                                  <Tooltip title={(dcp.standard || 'SMPTE').toUpperCase() === 'SMPTE'
                                    ? 'SMPTE — modern DCP standard (most theaters since ~2012)'
                                    : 'Interop — older DCP standard (legacy systems)'}>
                                    <Box component="span" sx={{ borderBottom: '1px dotted', borderColor: 'text.disabled', cursor: 'help' }}>{(dcp.standard || 'SMPTE').toUpperCase()}</Box>
                                  </Tooltip>{' · '}
                                  <Tooltip title={(dcp.content_type || 'FTR') === 'FTR' ? 'Feature' : (dcp.content_type === 'TRL' ? 'Trailer' : (dcp.content_type === 'TST' ? 'Test' : (dcp.content_type === 'EPS' ? 'Episode' : (dcp.content_type === 'PSA' ? 'PSA' : dcp.content_type))))}>
                                    <Box component="span" sx={{ borderBottom: '1px dotted', borderColor: 'text.disabled', cursor: 'help' }}>{dcp.content_type || 'FTR'}</Box>
                                  </Tooltip>
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {dcp.qc_status === 'pending' ? (
                          <Chip
                            size="small"
                            label="QUEUED"
                            color="default"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        ) : dcp.qc_status === 'running' ? (
                          <Box sx={{ minWidth: 110 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                              <Chip
                                size="small"
                                label="RUNNING"
                                color="info"
                                variant="filled"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {Math.round((dcp.qc_progress || 0) * 100)}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={Math.round((dcp.qc_progress || 0) * 100)}
                              sx={{ height: 4, borderRadius: 2 }}
                            />
                          </Box>
                        ) : dcp.qc_status ? (
                          <Tooltip title="Click for full QC report">
                            <Chip
                              icon={<QCIcon sx={{ fontSize: '14px !important' }} />}
                              label={dcp.qc_status.toUpperCase()}
                              size="small"
                              color={qcChipColor(dcp.qc_status)}
                              variant={dcp.qc_status === 'pass' ? 'outlined' : 'filled'}
                              onClick={() => openQcDrawer(dcp)}
                              sx={{ cursor: 'pointer' }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {hasPackage
                          ? <Tooltip title="Package is built and verified — ready to deliver to theaters">
                              <Chip icon={<PackageIcon sx={{ fontSize: '14px !important' }} />} label="Ready to send" size="small" color="success" variant="outlined" />
                            </Tooltip>
                          : <Chip icon={<CircularProgress size={10} sx={{ ml: '4px !important' }} />} label="Packaging…" size="small" variant="outlined" />
                        }
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const dcpKdms = (kdmRecords || []).filter(r => r.dcp_job_id === dcp.id && r.status === 'completed');
                          const kdmCount = dcpKdms.length;
                          if (!hasDelivery && kdmCount === 0) {
                            return (
                              <Tooltip title="Hasn't been sent to any theater yet">
                                <Typography variant="caption" color="text.secondary">Not yet sent</Typography>
                              </Tooltip>
                            );
                          }
                          // Number of unique theaters (recipient_email) with a delivery
                          const theaters = new Set((pkg?.shares || []).map(s => (s.recipient_email || '').toLowerCase()).filter(Boolean));
                          if (!dcp.encrypt) {
                            return (
                              <Tooltip title="Unencrypted DCP — no KDM/unlock-key needed; recipient can play directly">
                                <Chip
                                  label={shareCount === 0 ? 'Not yet sent' : `Sent to ${theaters.size} theater${theaters.size === 1 ? '' : 's'}`}
                                  size="small"
                                  color={shareCount > 0 ? 'success' : 'default'}
                                  variant={shareCount > 0 ? 'filled' : 'outlined'}
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              </Tooltip>
                            );
                          }
                          if (kdmCount === 0 && shareCount > 0) {
                            return (
                              <Tooltip title="DCP delivered but no Unlock Key (KDM) issued yet — venue can't play it until you issue one">
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                  <Chip label={`Sent · KDM pending`} size="small" color="warning" variant="filled" sx={{ height: 20, fontSize: '0.7rem' }} />
                                  <WarningIcon fontSize="small" color="warning" />
                                </Box>
                              </Tooltip>
                            );
                          }
                          if (kdmCount > 0 && shareCount === 0) {
                            return (
                              <Tooltip title={`${kdmCount} Unlock Key${kdmCount === 1 ? '' : 's'} (KDM) issued, but the DCP package itself hasn't been delivered yet`}>
                                <Chip label={`Key only · ${kdmCount}`} size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                              </Tooltip>
                            );
                          }
                          return (
                            <Tooltip title={`Sent to ${theaters.size} theater${theaters.size === 1 ? '' : 's'} with ${kdmCount} Unlock Key${kdmCount === 1 ? '' : 's'} (KDM)`}>
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Chip
                                  label={`Sent to ${theaters.size}`}
                                  size="small"
                                  color="success"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                  · {kdmCount} key{kdmCount === 1 ? '' : 's'}
                                </Typography>
                              </Box>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const shares = pkg?.shares || [];
                          if (shares.length === 0) {
                            return (
                              <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                                Not distributed yet
                              </Typography>
                            );
                          }
                          const totalViews = shares.reduce((n, s) => n + (s.view_count || 0), 0);
                          const totalStarted = shares.reduce((n, s) => n + (s.public_download_count || 0), 0);
                          const totalConfirmed = shares.reduce((n, s) => n + (s.confirmed_count || 0), 0);
                          const lastTs = shares
                            .map(s => s.activity_last_at || s.email_sent_at)
                            .filter(Boolean)
                            .map(t => new Date(t).getTime())
                            .reduce((a, b) => Math.max(a, b), 0);
                          const lastWhen = lastTs ? fmtDateTime(new Date(lastTs).toISOString()) : null;
                          if (totalViews + totalStarted + totalConfirmed === 0) {
                            return (
                              <Box>
                                <Typography variant="caption" color="text.secondary">No engagement yet</Typography>
                                {lastWhen && (
                                  <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', color: 'text.secondary' }}>
                                    sent {lastWhen}
                                  </Typography>
                                )}
                              </Box>
                            );
                          }
                          // Pick the strongest signal as headline.
                          let headline = '';
                          let color = 'text.secondary';
                          if (totalConfirmed > 0) {
                            headline = `${totalConfirmed} confirmed`;
                            color = 'success.main';
                          } else if (totalStarted > 0) {
                            headline = `${totalStarted} started`;
                            color = 'info.main';
                          } else {
                            headline = `${totalViews} viewed`;
                          }
                          return (
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 500, color }}>
                                {headline}
                              </Typography>
                              <Tooltip
                                arrow placement="top"
                                title={
                                  <Box sx={{ p: 0.5, fontSize: '0.75rem', lineHeight: 1.5 }}>
                                    <div><strong>{totalViews} viewed</strong> — opened the share landing page</div>
                                    <div><strong>{totalStarted} started</strong> — clicked the Download button</div>
                                    <div><strong>{totalConfirmed} complete</strong> — finished downloading (full file served, verified)</div>
                                  </Box>
                                }
                              >
                                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', color: 'text.secondary', cursor: 'help' }}>
                                  {totalViews} viewed · {totalStarted} started · {totalConfirmed} complete
                                </Typography>
                              </Tooltip>
                              {lastWhen && (
                                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', color: 'text.secondary' }}>
                                  last {lastWhen}
                                </Typography>
                              )}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {dcp.completed_at ? (
                          <Tooltip title={fmtDateTime(dcp.completed_at)} arrow placement="top">
                            <Typography variant="caption" sx={{ cursor: 'help' }}>
                              {formatRelativeTime(dcp.completed_at)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
                        {hasPackage && (
                          <Tooltip title="Open Distribution page for this DCP — create / manage delivery links + KDMs">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/distribution?dcp=${dcp.id}`)}
                            >
                              <DeliverIcon fontSize="small" color="primary" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPackage && (
                          <Tooltip title="Delivery analytics — per-recipient lifecycle, downloads, transfer telemetry">
                            <IconButton
                              size="small"
                              onClick={() => setAnalyticsDcp(dcp)}
                            >
                              <AnalyticsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPackage && pkg && canDelete && (
                          <Tooltip title="Download package to your workstation — for alternate delivery (portable drive, alternate uplink). Doesn't show up in recipient analytics.">
                            <IconButton
                              size="small"
                              onClick={async () => {
                                try {
                                  const { data } = await api.get(`/api/delivery/packages/${pkg.id}/operator-download`);
                                  if (data?.url) {
                                    window.location.assign(data.url);
                                  } else {
                                    setSnackbar({ open: true, message: 'Download URL was empty', severity: 'error' });
                                  }
                                } catch (e) {
                                  const msg = e?.response?.data?.detail || e?.message || 'Download failed';
                                  setSnackbar({ open: true, message: msg, severity: 'error' });
                                }
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* Legacy KDM-only distribute page kept for any
                            in-flight workflows; surfaced as a small
                            secondary icon next to the unified Deliver. */}
                        {dcp.encrypt && (
                          <Tooltip title="Legacy KDM-only page (per-screen KDM, no delivery link)">
                            <IconButton size="small" onClick={() => navigate(`/dcp/${dcp.id}/distribute`)}
                              sx={{ opacity: 0.4, '&:hover': { opacity: 1 } }}>
                              <KDMIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && dcp.deleted_at && (
                          <>
                          <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
                          <Tooltip title="Restore from archive">
                            <IconButton size="small" sx={{ color: '#43a047' }} onClick={async () => {
                              try {
                                await dispatch(restoreDCPJob(dcp.id)).unwrap();
                                setSnackbar({ open: true, message: `"${dcp.name}" restored`, severity: 'success' });
                                dispatch(fetchDCPJobs({ includeArchived: showArchived }));
                              } catch (e) {
                                setSnackbar({ open: true, message: `Restore failed: ${typeof e === 'string' ? e : (e?.message || 'unknown error')}`, severity: 'error' });
                              }
                            }}>
                              <UnarchiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          </>
                        )}
                        {canDelete && !dcp.deleted_at && (
                          <>
                          <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
                          <Tooltip title="Archive (preserves metrics; restorable)">
                            <IconButton size="small" onClick={async () => {
                              if (!window.confirm(`Archive "${dcp.name}"? It moves out of the active Library and is preserved for reports. You can restore from the "Show archived" view.`)) return;
                              const tryDelete = async (force) => {
                                if (pkg && !force) {
                                  try { await dispatch(revokePackage(pkg.id)).unwrap(); }
                                  catch (_) { /* best-effort; the delete handler does the heavy lifting */ }
                                }
                                await dispatch(deleteDCPJob({ jobId: dcp.id, force })).unwrap();
                              };
                              try {
                                await tryDelete(false);
                                setSnackbar({ open: true, message: `"${dcp.name}" deleted`, severity: 'success' });
                                dispatch(fetchDCPJobs());
                              } catch (e) {
                                // Backend says: this DCP has active KDMs/deliveries.
                                // Show a stronger confirmation, then retry with force=true.
                                if (e && typeof e === 'object' && e.status === 409 && e.code === 'dcp_has_active_attachments') {
                                  const k = e.active_kdms || 0;
                                  const s = e.active_shares || 0;
                                  const parts = [];
                                  if (k > 0) parts.push(`${k} KDM${k === 1 ? '' : 's'}`);
                                  if (s > 0) parts.push(`${s} active deliver${s === 1 ? 'y' : 'ies'}`);
                                  const detail = parts.join(' and ');
                                  const hardOk = window.confirm(
                                    `"${dcp.name}" has ${detail} attached.\n\n` +
                                    `Deleting now will:\n` +
                                    `  • soft-delete the KDM record(s) — they'll stay in the audit trail but disappear from the KDM list\n` +
                                    `  • hard-delete the package(s) and share link(s)\n\n` +
                                    `Type OK in the next prompt to proceed.`
                                  );
                                  if (!hardOk) {
                                    setSnackbar({ open: true, message: 'Delete cancelled', severity: 'info' });
                                    return;
                                  }
                                  const confirm = window.prompt(`Type OK to permanently delete "${dcp.name}":`);
                                  if (confirm !== 'OK') {
                                    setSnackbar({ open: true, message: 'Delete cancelled — confirmation text did not match', severity: 'info' });
                                    return;
                                  }
                                  try {
                                    await tryDelete(true);
                                    setSnackbar({ open: true, message: `"${dcp.name}" deleted (KDM history preserved)`, severity: 'success' });
                                    dispatch(fetchDCPJobs());
                                  } catch (e2) {
                                    setSnackbar({
                                      open: true,
                                      message: `Delete failed: ${typeof e2 === 'string' ? e2 : (e2?.message || 'unknown error')}`,
                                      severity: 'error',
                                    });
                                  }
                                } else {
                                  setSnackbar({
                                    open: true,
                                    message: typeof e === 'string' ? `Delete failed: ${e}` : `Delete failed: ${e?.message || 'unknown error'}`,
                                    severity: 'error',
                                  });
                                }
                              }
                            }}>
                              <DeleteIcon fontSize="small" color="error" />
                            </IconButton>
                          </Tooltip>
                          </>
                        )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={bulkVisible ? 9 : 8} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                          {(() => {
                            const dtab = detailTab[dcp.id] || 'delivery';
                            return (
                          <>
                          <Tabs
                            value={dtab}
                            onChange={(_, v) => setDetailTab(d => ({ ...d, [dcp.id]: v }))}
                            sx={{ mt: 1, minHeight: 36, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: '0.8rem', textTransform: 'none', letterSpacing: 0 } }}
                          >
                            <Tab value="delivery" label="Delivery" />
                            <Tab value="technical" label="Technical" />
                          </Tabs>
                          <Box sx={{ py: 2, px: 1, display: dtab === 'technical' ? 'grid' : 'block', gridTemplateColumns: dtab === 'technical' ? { xs: '1fr', md: 'repeat(3, 1fr)' } : undefined, gap: 2 }}>
                          {dtab === 'technical' && (<>
                            <Box>
                              <Typography variant="overline" color="text.secondary">Format</Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                <Chip label={dcp.resolution} size="small" variant="outlined" />
                                <Chip label={dcp.container} size="small" variant="outlined" />
                                <Chip label={`${dcp.frame_rate || 24} fps`} size="small" variant="outlined" />
                                <Chip label={(dcp.standard || 'SMPTE').toUpperCase()} size="small" variant="outlined" />
                                <Chip label={dcp.content_type || 'FTR'} size="small" variant="outlined" />
                              </Box>
                            </Box>
                            <Box>
                              <Typography variant="overline" color="text.secondary">Audio</Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                <Chip label={dcp.audio_layout === 'stereo' ? 'Stereo (2.0)' : '5.1 surround'} size="small" variant="outlined" />
                                {dcp.audio_language && <Chip label={dcp.audio_language} size="small" variant="outlined" />}
                              </Box>
                            </Box>
                            <Box>
                              <Typography variant="overline" color="text.secondary">Security</Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                {dcp.encrypt
                                  ? <Chip label="Encrypted" size="small" color="warning" />
                                  : <Chip label="Open (no KDM)" size="small" variant="outlined" />}
                              </Box>
                            </Box>

                            <Box>
                              <Typography variant="overline" color="text.secondary">Source file</Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all', mt: 0.5 }}>
                                {dcp.source_filename || '—'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="overline" color="text.secondary">Reels</Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {dcp.reel_mode === 'single' ? 'Single reel'
                                  : dcp.reel_mode === 'by_duration' ? `Split by duration (${dcp.reel_duration_minutes || 20} min)`
                                  : dcp.reel_mode === 'by_timecode' ? 'Split at timecodes'
                                  : dcp.reel_mode === 'by_source_file' ? 'One reel per source file'
                                  : dcp.reel_mode || '—'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="overline" color="text.secondary">Markers</Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {(dcp.ffec_frame || dcp.ffmc_frame)
                                  ? `FFEC frame ${dcp.ffec_frame ?? '—'}, FFMC frame ${dcp.ffmc_frame ?? '—'}`
                                  : 'Not set'}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="overline" color="text.secondary">Created</Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {dcp.created_at ? new Date(dcp.created_at).toLocaleString() : '—'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="overline" color="text.secondary">Encoded</Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {dcp.encoding_completed_at ? new Date(dcp.encoding_completed_at).toLocaleString() : '—'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="overline" color="text.secondary">Packaged</Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {dcp.packaging_completed_at ? new Date(dcp.packaging_completed_at).toLocaleString() : '—'}
                              </Typography>
                            </Box>

                            {/* Package info: size + checksums for chain-of-custody.
                                Hidden until the package row exists (sync hasn't
                                created it yet) or hashes haven't been computed. */}
                            {pkg && (pkg.size_bytes || pkg.md5 || pkg.sha256) && (
                              <Box sx={{ gridColumn: '1 / -1', mt: 1, p: 1.25, bgcolor: 'background.default', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                                <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  Package · Verification
                                </Typography>
                                {pkg.size_bytes && (
                                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                    <strong>Size:</strong> {(pkg.size_bytes / (1024**3)).toFixed(2)} GB ({pkg.size_bytes.toLocaleString()} bytes)
                                  </Typography>
                                )}
                                {pkg.md5 ? (
                                  <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem', cursor: 'pointer', userSelect: 'all' }} onClick={() => navigator.clipboard?.writeText(pkg.md5)}>
                                    <strong style={{ fontFamily: 'inherit', fontSize: '0.7rem' }}>MD5:</strong> {pkg.md5}
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                    <strong>MD5:</strong> computing…
                                  </Typography>
                                )}
                                {pkg.sha256 ? (
                                  <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem', cursor: 'pointer', userSelect: 'all' }} onClick={() => navigator.clipboard?.writeText(pkg.sha256)}>
                                    <strong style={{ fontFamily: 'inherit', fontSize: '0.7rem' }}>SHA-256:</strong> {pkg.sha256}
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                    <strong>SHA-256:</strong> computing…
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </>)}
                          {dtab === 'delivery' && (
                            <Box sx={{ py: 2, px: 1 }}>
                              {/* Distribution moved to /distribution in v9.22.0. The expanded
                                  panel here used to render every recipient, every share, and
                                  every KDM in stacked tables — operators couldn't scan it.
                                  Now Library is just the catalog; the same dialog the inline
                                  panel used to expose lives at /distribution with this DCP
                                  pre-selected, and the new Distributions tab there shows the
                                  full audit across every DCP. */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<DeliverIcon />}
                                  onClick={() => navigate(`/distribution?dcp=${dcp.id}`)}
                                >
                                  Manage distribution
                                </Button>
                                <Typography variant="caption" color="text.secondary">
                                  {(pkg?.shares || []).length} share{(pkg?.shares || []).length === 1 ? '' : 's'}
                                  {' · '}
                                  {(kdmRecords || []).filter(r => r.dcp_job_id === dcp.id).length} KDM
                                  {(kdmRecords || []).filter(r => r.dcp_job_id === dcp.id).length === 1 ? '' : 's'}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          </Box>
                          </>
                          );
                          })()}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Drawer anchor="right" open={!!qcDrawer} onClose={() => setQcDrawer(null)}
        PaperProps={{ sx: { width: { xs: '100vw', md: 640 }, p: 3 } }}>
        {qcDrawer && (
          <Box>
            <Typography variant="h6" gutterBottom>QC Report — {qcDrawer.job?.name}</Typography>
            {qcDrawer.report?.qc_completed_at && (
              <Typography variant="caption" color="text.secondary">
                Completed: {new Date(qcDrawer.report.qc_completed_at).toLocaleString()}
              </Typography>
            )}
            <Box sx={{ my: 2, display: 'flex', gap: 1 }}>
              <Chip label={`Status: ${qcDrawer.report?.qc_status || qcDrawer.job?.qc_status || 'unknown'}`}
                color={qcChipColor(qcDrawer.report?.qc_status || qcDrawer.job?.qc_status)} />
              {qcDrawer.report?.report?.summary && (
                <>
                  <Chip label={`${qcDrawer.report.report.summary.pass} pass`} variant="outlined" color="success" />
                  <Chip label={`${qcDrawer.report.report.summary.warn} warn`} variant="outlined" color="warning" />
                  <Chip label={`${qcDrawer.report.report.summary.fail} fail`} variant="outlined" color="error" />
                </>
              )}
              <Box sx={{ flex: 1 }} />
              <Button size="small" onClick={() => rerunQc(qcDrawer.job.id)} disabled={qcDrawer.loading}>Re-run QC</Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {qcDrawer.loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  {qcDrawer.qcProgress || 'Running…'}
                </Typography>
              </Box>
            )}
            {qcDrawer.error && <Alert severity="error">{qcDrawer.error}</Alert>}
            {qcDrawer.report?.report && (() => {
              const r = qcDrawer.report.report;
              const cmChecks = r.conformance?.checks || [];
              const verifyErrors = r.spec_verification?.errors || [];
              const verifyWarnings = r.spec_verification?.warnings || [];

              // The backend already categorizes each check and provides a clean label.
              const groups = {};
              cmChecks.forEach(c => {
                const cat = c.category || 'Other structural checks';
                if (!groups[cat]) groups[cat] = { pass: 0, warn: 0, fail: 0 };
                if (c.status === 'pass') groups[cat].pass += 1;
                else if (c.status === 'warn') groups[cat].warn += 1;
                else if (c.status === 'fail') groups[cat].fail += 1;
              });

              const failing = cmChecks.filter(c => c.status !== 'pass');
              const totalPass = cmChecks.filter(c => c.status === 'pass').length;

              return (
                <Box>
                  {(failing.length > 0 || verifyErrors.length > 0 || verifyWarnings.length > 0) && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Findings</Typography>
                      <Box component="ul" sx={{ pl: 2, my: 1 }}>
                        {failing.map((c, i) => (
                          <li key={`cm${i}`}>
                            <Typography variant="caption" color={c.status === 'fail' ? 'error' : 'warning.main'}>
                              <strong>{c.status.toUpperCase()}</strong> — {c.label || c.category}{c.message ? `: ${c.message}` : ''}
                            </Typography>
                          </li>
                        ))}
                        {verifyErrors.map((e, i) => (
                          <li key={`ve${i}`}><Typography variant="caption" color="error"><strong>FAIL</strong> — {e}</Typography></li>
                        ))}
                        {verifyWarnings.map((w, i) => (
                          <li key={`vw${i}`}><Typography variant="caption" color="warning.main"><strong>WARN</strong> — {w}</Typography></li>
                        ))}
                      </Box>
                    </>
                  )}

                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Checks performed</Typography>
                  {(() => {
                    // Detect Interop from the job's standard field (most reliable)
                    // or from the QC notes as a fallback.
                    const svNotes = r.spec_verification?.notes || [];
                    const isInterop = (qcDrawer.job?.standard || '').toLowerCase() === 'interop' ||
                      svNotes.some(n => /does not use the smpte|interop/i.test(n || ''));
                    return (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {totalPass} structural and conformance checks passed across {Object.keys(groups).length} categories.
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, my: 1 }}>
                          {Object.entries(groups).sort().map(([label, counts]) => (
                            <li key={label}>
                              <Typography variant="caption" color="text.secondary">
                                {label}: <strong>{counts.pass}</strong> passed
                                {counts.warn > 0 && <span style={{ color: '#ed6c02' }}> · {counts.warn} warning</span>}
                                {counts.fail > 0 && <span style={{ color: '#d32f2f' }}> · {counts.fail} failed</span>}
                              </Typography>
                            </li>
                          ))}
                          <li>
                            <Typography variant="caption" color="text.secondary">
                              {isInterop
                                ? 'This is an Interop DCP. SMPTE Bv2.1 conformance checks do not apply.'
                                : <>SMPTE Bv2.1 spec verification:{' '}
                                    {(verifyErrors.length === 0 && verifyWarnings.length === 0)
                                      ? <strong>clean</strong>
                                      : <span>{verifyErrors.length} error(s), {verifyWarnings.length} warning(s)</span>}
                                  </>}
                            </Typography>
                          </li>
                        </Box>
                      </>
                    );
                  })()}

                  <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
                    The full QC report is also written into the DCP directory (qc_report.json and qc_report.txt) and tarred with the package, so downstream recipients can verify everything was checked.
                  </Typography>
                </Box>
              );
            })()}
          </Box>
        )}
      </Drawer>

      {deliverJob && (
        <DeliverDialog
          open={Boolean(deliverJob)}
          onClose={() => { setDeliverJob(null); setDeliverPhysical(false); }}
          sourceType="dcp"
          sourceJobId={deliverJob.id}
          projectName={deliverJob.name}
          filename={deliverJob.package_path ? deliverJob.package_path.split('/').pop() : `${deliverJob.name}.tar`}
          nasPath={deliverJob.package_path || ''}
          jobData={deliverJob}
          defaultPhysical={deliverPhysical}
        />
      )}

      {distributeJob && (
        <DCPDistributeDialog
          open={Boolean(distributeJob)}
          onClose={() => {
            setDistributeJob(null);
            // Refresh KDMs + Packages so the row's Distribution column
            // and expanded detail update immediately.
            dispatch(fetchKDMRecords({ dcpJobId: distributeJob.id }));
            dispatch(fetchPackages({ sourceType: 'dcp' }));
          }}
          dcp={distributeJob}
        />
      )}

      {/* KDM Email Dialog */}
      <Dialog open={!!kdmEmailDialog} onClose={() => setKdmEmailDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Email KDM to {kdmEmailDialog?.venue_name} — {kdmEmailDialog?.screen_name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Sending to: <strong>{kdmEmailDialog?.venue_name}</strong> ({kdmEmailDialog?.screen_name})
            </Typography>
            <TextField label="CC" fullWidth value={kdmEmailForm.cc}
              onChange={(e) => setKdmEmailForm(f => ({ ...f, cc: e.target.value }))}
              placeholder="Optional — comma-separate for multiple"
              InputLabelProps={{ shrink: Boolean(kdmEmailForm.cc) || undefined }}
            />
            <TextField label="Note to venue" fullWidth multiline minRows={3} maxRows={8}
              value={kdmEmailForm.message}
              onChange={(e) => setKdmEmailForm(f => ({ ...f, message: e.target.value }))}
              placeholder="e.g. Please ingest this KDM before the April 30 screening."
              InputLabelProps={{ shrink: Boolean(kdmEmailForm.message) || undefined }}
              sx={{ '& .MuiOutlinedInput-root': { height: 'auto !important' } }}
            />
            {/* v10.19.58: opt-in KDM file attachment. Default off
                because some corporate mail filters (Defender ATP,
                Mimecast, Proofpoint) strip .kdm attachments. The
                download link is always included; with attach=true the
                file rides along and the body advertises the link as
                a fallback. */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!kdmEmailForm.attach}
                  onChange={(_e, v) => setKdmEmailForm(f => ({ ...f, attach: v }))}
                />
              }
              label="Attach the .kdm file to the email"
              sx={{ alignItems: 'flex-start' }}
            />
            {kdmEmailForm.attach && (
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: -1, ml: 4 }}>
                Some venues' mail filters strip .kdm files. The download link goes along as a fallback either way.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKdmEmailDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={async () => {
            try {
              await api.post(`/api/kdm/records/${kdmEmailDialog.id}/email`, {
                cc_email: kdmEmailForm.cc || undefined,
                message: kdmEmailForm.message || undefined,
                attach: !!kdmEmailForm.attach,
              });
              alert(`KDM emailed to ${kdmEmailDialog.venue_name} / ${kdmEmailDialog.screen_name}${kdmEmailForm.attach ? ' (with attachment)' : ''}`);
              setKdmEmailDialog(null);
              setKdmEmailForm({ cc: '', message: '', attach: false });
            } catch (e) {
              alert(e?.response?.data?.detail || 'Failed to send');
            }
          }}>Send KDM</Button>
        </DialogActions>
      </Dialog>

      <DCPDeliveryAnalyticsDrawer
        open={!!analyticsDcp}
        dcp={analyticsDcp}
        onClose={() => setAnalyticsDcp(null)}
      />

      <Dialog open={!!bulkAction} onClose={() => setBulkAction(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {bulkAction?.kind === 'archive' && `Archive ${selectedDcpIds.size} DCPs?`}
          {bulkAction?.kind === 'rerun_qc' && `Re-run QC on ${selectedDcpIds.size} DCPs?`}
          {bulkAction?.kind === 'revoke' && `Revoke shares on ${selectedDcpIds.size} DCPs?`}
        </DialogTitle>
        <DialogContent>
          {bulkAction?.kind === 'archive' && (
            <Typography variant="body2">
              Archive frees the build directory, package tar/zip, MinIO objects,
              and KDM XML sidecars. The DCP rows are stamped <code>deleted_at</code>
              but the metadata + history are preserved. Recoverable via "Show
              archived"; rebuilding the DCP would need a fresh encode from source.
            </Typography>
          )}
          {bulkAction?.kind === 'rerun_qc' && (
            <Typography variant="body2">
              Queues fresh Clairmeta + dcpomatic2_verify QC runs on the selected
              DCPs. QC takes 5–10 minutes per 4K feature; the chips will update
              as each completes.
            </Typography>
          )}
          {bulkAction?.kind === 'revoke' && (
            <Typography variant="body2">
              Revokes every active package + share on the selected DCPs.
              Recipients get HTTP 410 if they try the link. KDMs are NOT
              affected (they don't expire just because a share is revoked).
              Cannot be undone.
            </Typography>
          )}
          <Box sx={{ mt: 2, p: 1.5, maxHeight: 200, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {Array.from(selectedDcpIds).slice(0, 50).map((id) => {
              const d = (jobs || []).find((x) => x.id === id);
              if (!d) return null;
              return (
                <Typography key={id} variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
                  {d.name || d.unique_id}
                </Typography>
              );
            })}
            {selectedDcpIds.size > 50 && (
              <Typography variant="caption" color="text.secondary">
                …and {selectedDcpIds.size - 50} more
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkAction(null)} disabled={bulk.busy}>Cancel</Button>
          <Button
            color={bulkAction?.kind === 'archive' ? 'error' : bulkAction?.kind === 'revoke' ? 'warning' : 'primary'}
            variant="contained"
            disabled={bulk.busy}
            onClick={() => {
              if (bulkAction?.kind === 'archive') doBulkArchive();
              else if (bulkAction?.kind === 'rerun_qc') doBulkRerunQc();
              else if (bulkAction?.kind === 'revoke') doBulkRevoke();
            }}
          >
            {bulkAction?.kind === 'archive' ? 'Archive' :
             bulkAction?.kind === 'revoke' ? 'Revoke' : 'Re-run'} {selectedDcpIds.size}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Surface delete/rename failures so they don't fail silently. */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DCPLibrary;
